import { AlertTriangle, MapPin, ShieldOff, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppState } from '../../context/AppStateContext';
import { Badge } from '../common/Badge';
import { PageHeader } from '../common/PageHeader';
import { ui } from '../../styles/ui';

const RESULT_META = {
  FAILED: { label: 'Failed', tone: 'danger', icon: XCircle },
  INVALID: { label: 'Invalid Code', tone: 'neutral', icon: ShieldOff },
};

export const FailedScans = () => {
  const { failedScans, loadFailedScans } = useAppState();
  const [searchTerm, setSearchTerm] = useState('');
  const [resultFilter, setResultFilter] = useState('all');

  // Refetch every time this tab is opened, not just once at login.
  useEffect(() => {
    loadFailedScans(undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return failedScans.filter((log) => {
      const matchesResult = resultFilter === 'all' || log.result === resultFilter;
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        term === '' ||
        log.code?.toLowerCase().includes(term) ||
        log.mobile?.toLowerCase().includes(term) ||
        `${log.scannedBy?.firstName || ''} ${log.scannedBy?.lastName || ''}`.toLowerCase().includes(term);
      return matchesResult && matchesSearch;
    });
  }, [failedScans, searchTerm, resultFilter]);

  return (
    <div className="space-y-4">
      <PageHeader title="Failed Scans" subtitle="Scan attempts that were rejected before verification — forged/unknown codes, signature mismatches" />

      <div className="flex flex-col sm:flex-row gap-2.5">
        <input
          type="text"
          placeholder="Search by code, mobile, or scanner name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`flex-1 ${ui.input}`}
        />
        <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value)} className={ui.input + ' sm:w-44'}>
          <option value="all">All Failures</option>
          <option value="FAILED">Failed</option>
          <option value="INVALID">Invalid Code</option>
        </select>
      </div>

      <div className={`${ui.card} overflow-hidden`}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing <span className="font-semibold text-gray-900 dark:text-white">{filtered.length}</span> failed scan{filtered.length === 1 ? '' : 's'}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-400 dark:text-gray-500">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <span className="text-sm">No failed scans found.</span>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className={ui.tableHeadCell}>Scanned By</th>
                    <th className={ui.tableHeadCell}>Code</th>
                    <th className={ui.tableHeadCell}>Result</th>
                    <th className={ui.tableHeadCell}>Reason</th>
                    <th className={ui.tableHeadCell}>When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                  {filtered.map((log) => {
                    const meta = RESULT_META[log.result] || RESULT_META.FAILED;
                    return (
                      <tr key={log._id} className={ui.tableRow}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {log.scannedBy ? `${log.scannedBy.firstName} ${log.scannedBy.lastName}` : 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {log.scannedBy?.role || ''} &middot; {log.mobile}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono font-semibold break-all">{log.code}</code>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge tone={meta.tone} icon={meta.icon}>{meta.label}</Badge>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <div className="text-sm text-gray-700 dark:text-gray-300">{log.rejectionReason || '—'}</div>
                          {log.meta?.location?.lat != null && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 flex items-center mt-1">
                              <MapPin className="h-3 w-3 mr-1" />
                              {log.meta.location.lat.toFixed(4)}, {log.meta.location.lng.toFixed(4)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-300">{new Date(log.createdAt).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{new Date(log.createdAt).toLocaleTimeString()}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700/60">
              {filtered.map((log) => {
                const meta = RESULT_META[log.result] || RESULT_META.FAILED;
                return (
                  <div key={log._id} className="p-3.5 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {log.scannedBy ? `${log.scannedBy.firstName} ${log.scannedBy.lastName}` : 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{log.mobile}</div>
                      </div>
                      <Badge tone={meta.tone} icon={meta.icon}>{meta.label}</Badge>
                    </div>
                    <code className="block px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono break-all w-fit">{log.code}</code>
                    {log.rejectionReason && <div className="text-sm text-gray-700 dark:text-gray-300">{log.rejectionReason}</div>}
                    <div className="text-xs text-gray-400 dark:text-gray-500">{new Date(log.createdAt).toLocaleString()}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
